import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API = "https://api.reignitespirit.com";
//const API = "http://localhost:8080";

export default function AdminPremiumDashboard() {
  const [email, setEmail] = useState("");
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);

  const [lookupEmail, setLookupEmail] = useState("");
  const [lookupResult, setLookupResult] = useState(null);

  const [regenReports, setRegenReports] = useState([]);
  const [regenerating, setRegenerating] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingRegen, setLoadingRegen] = useState(false);

const [blogPosts, setBlogPosts] = useState([]);
const [loadingBlogs, setLoadingBlogs] = useState(false);
const [evaluatingQuality, setEvaluatingQuality] = useState(false);
const [screeningSources, setScreeningSources] = useState(false);

const [pendingComments, setPendingComments] = useState([]);
const [loadingComments, setLoadingComments] = useState(false);
const [blogCategories, setBlogCategories] = useState([]);
const [categoryForm, setCategoryForm] = useState({
  name: "",
  slug: "",
  description: "",
});

const [blogForm, setBlogForm] = useState({
  id: null,
  isFeatured: false,
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  coverImageUrl: "",
  authorName: "ReigniteSpirit",
  status: "draft",
  categoryId: "",

  // SEO METADATA
  seoTitle: "",
  seoDescription: "",
  primaryKeyword: "",

  // PHASE 6B - ARTICLE QUALITY
  researchQualityScore: null,
  seoQualityScore: null,
  careerRelevanceScore: null,
  originalityScore: null,
  sourceAuthorityScore: null,
  conversionPotentialScore: null,
  articleQualityScore: null,
  qualityRecommendation: "",
  qualityEvaluatedAt: null,

  // PHASE 2 - GROUNDED SOURCES
  sources: [],
});
  const navigate = useNavigate();

  const getToken = () => localStorage.getItem("adminToken");

  useEffect(() => {
    if (!getToken()) navigate("/admin-login");
  }, [navigate]);

  // =====================================================
  // LOAD DATA
  // =====================================================
  const load = useCallback(async () => {
    try {
      const token = getToken();
      if (!token) return;

      const res = await axios.get(`${API}/api/admin/investor-whitelist`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setItems(res.data || []);
    } catch (err) {
      console.error("Failed to load whitelist", err);
      setItems([]);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);

      const token = getToken();
      if (!token) return;

      const res = await axios.get(`${API}/api/admin/all-users-report-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setUsers(res.data || []);
    } catch (err) {
      console.error("Failed to load users", err);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadUsers();
  }, [load, loadUsers]);

  // =====================================================
  // WHITELIST
  // =====================================================
  const add = async () => {
    try {
      const token = getToken();
      if (!token) return;

      await axios.post(
        `${API}/api/admin/investor-whitelist`,
        { email },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setEmail("");
      load();
    } catch (err) {
      console.error(err);
      alert("Failed to add investor");
    }
  };

  const disable = async (targetEmail) => {
    try {
      const token = getToken();
      if (!token) return;

      await axios.post(
        `${API}/api/admin/investor-whitelist/disable`,
        { email: targetEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      load();
    } catch (err) {
      console.error(err);
      alert("Failed to disable investor");
    }
  };

  // =====================================================
  // ORIGINAL REPORT
  // =====================================================
  const handleViewOriginalReport = () => {
    if (!lookupResult?.pdfUrl) {
      alert("Original report not available");
      return;
    }

    window.open(lookupResult.pdfUrl, "_blank");
  };

  const handleViewUserReport = (pdfUrl) => {
    if (!pdfUrl) {
      alert("Report not available");
      return;
    }

    window.open(pdfUrl, "_blank");
  };

  // =====================================================
  // FETCH REGENERATED REPORTS
  // =====================================================
  const fetchRegeneratedReports = async (assessmentId, emailOverride) => {
  try {
    if (!assessmentId && !emailOverride) {
      setRegenReports([]);
      return;
    }

    setLoadingRegen(true);

    const token = getToken();
    if (!token) return;

    const res = await axios.get(`${API}/api/admin/regenerated-reports`, {
      params: {
        assessmentId,
        email: emailOverride || lookupResult?.email || "",
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    console.log("REGENERATED REPORTS API RESPONSE:", res.data);
    setRegenReports(Array.isArray(res.data) ? res.data : []);
  } catch (err) {
    console.error("Failed to load regenerated reports", err);
    setRegenReports([]);
  } finally {
    setLoadingRegen(false);
  }
};

const lookupUser = async (overrideEmail) => {
  try {
    const token = getToken();
    if (!token) return;

    const targetEmail = (overrideEmail || lookupEmail || "").trim();
    if (!targetEmail) {
      alert("Please enter email");
      return;
    }

    const res = await axios.post(
      `${API}/api/admin/user-lookup`,
      { email: targetEmail },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    const result = res.data;

    console.log("LOOKUP RESULT:", result);

    setLookupEmail(targetEmail);
    setLookupResult(result);

    if (result?.assessmentId) {
      await fetchRegeneratedReports(result.assessmentId, result.email);
    } else {
      await fetchRegeneratedReports(null, result?.email || targetEmail);
    }
  } catch (err) {
    console.error(err);
    setLookupResult(null);
    setRegenReports([]);
    alert("Lookup failed");
  }
};

const regenerateReport = async () => {
  try {
    if (!lookupResult?.email || !lookupResult?.assessmentId) {
      alert("Assessment not available for regeneration");
      return;
    }

    setRegenerating(true);

    const token = getToken();
    if (!token) return;

    const regenRes = await axios.post(
      `${API}/api/admin/regenerate-report`,
      {
        email: lookupResult.email,
        assessmentId: lookupResult.assessmentId,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    console.log("REGENERATE RESPONSE:", regenRes.data);

    if (regenRes?.data?.pdfUrl) {
      setRegenReports((prev) => [
        {
          id: `temp-${Date.now()}`,
          assessmentId: regenRes?.data?.assessmentId || lookupResult.assessmentId,
          email: lookupResult.email,
          pdfUrl: regenRes.data.pdfUrl,
          version: regenRes.data.version ?? prev.length + 1,
          createdAt: new Date().toISOString(),
        },
        ...prev,
      ]);
    }

    await fetchRegeneratedReports(
      regenRes?.data?.assessmentId || lookupResult.assessmentId,
      lookupResult.email
    );

    await loadUsers();

    alert("Report regenerated successfully!");
  } catch (err) {
    console.error(err);

    const serverMessage =
      err?.response?.data ||
      err?.message ||
      "Failed to regenerate report";

    alert(
      typeof serverMessage === "string"
        ? serverMessage
        : JSON.stringify(serverMessage)
    );
  } finally {
    setRegenerating(false);
  }
};
 const handleQuickLookup = async (user) => {
    await lookupUser(user.email);
  };

const deleteRegisteredUser = async (user) => {
  try {
    const email =
      (user?.email || "")
        .trim();

    if (!email) {
      alert(
        "User email is missing."
      );

      return;
    }

    const confirmed =
      window.confirm(
        `Delete ${email} permanently?\n\n` +
        "The user's login account will also be deleted and their old credentials will no longer work."
      );

    if (!confirmed) {
      return;
    }

    const token =
      getToken();

    if (!token) {
      alert(
        "Admin session expired. Please login again."
      );

      navigate(
        "/admin-login"
      );

      return;
    }

    await axios.delete(
      `${API}/api/admin/users`,
      {
        headers: {
          Authorization:
            `Bearer ${token}`,
        },

        data: {
          email,
        },
      }
    );

    // Remove immediately from screen
    setUsers(
      (previousUsers) =>
        previousUsers.filter(
          (u) =>
            (
              u.email || ""
            )
              .toLowerCase() !==
            email.toLowerCase()
        )
    );

    // Clear lookup if same user was open
    if (
      lookupResult?.email
        ?.toLowerCase() ===
      email.toLowerCase()
    ) {
      setLookupResult(
        null
      );

      setRegenReports(
        []
      );

      setLookupEmail(
        ""
      );
    }

    // Reload from server to confirm
    await loadUsers();

    alert(
      "User deleted successfully."
    );
  }
  catch (err) {
    console.error(
      "DELETE REGISTERED USER ERROR:",
      err
    );

    const response =
      err?.response?.data;

    const message =
      response?.message ||
      response?.detail ||
      (
        typeof response ===
        "string"
          ? response
          : null
      ) ||
      err?.message ||
      "Unable to delete user.";

    alert(message);
  }
};

const loadBlogCategories = useCallback(async () => {
  try {
    const token = getToken();
    if (!token) return;

    const res = await axios.get(`${API}/api/admin/blog-categories`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    setBlogCategories(res.data || []);
  } catch (err) {
    console.error("Failed to load blog categories", err);
    setBlogCategories([]);
  }
}, []);

useEffect(() => {
  loadBlogCategories();
}, [loadBlogCategories]);

const saveBlogCategory = async () => {
  try {
    const token = getToken();
    if (!token) return;

    if (!categoryForm.name.trim()) {
      alert("Category name is required");
      return;
    }

    await axios.post(
      `${API}/api/admin/blog-categories`,
      {
        name: categoryForm.name.trim(),
        slug: categoryForm.slug.trim() || makeSlug(categoryForm.name),
        description: categoryForm.description,
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setCategoryForm({ name: "", slug: "", description: "" });
    await loadBlogCategories();

    alert("Category added successfully");
  } catch (err) {
    console.error(err);
    alert(err?.response?.data || "Failed to save category");
  }
};

 const loadBlogs = useCallback(async () => {
  try {
    setLoadingBlogs(true);
    const token = getToken();
    if (!token) return;

    const res = await axios.get(`${API}/api/admin/blog`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    setBlogPosts(res.data || []);
  } catch (err) {
    console.error("Failed to load blogs", err);
    setBlogPosts([]);
  } finally {
    setLoadingBlogs(false);
  }
}, []);

useEffect(() => {
  loadBlogs();
}, [loadBlogs]);

const makeSlug = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const saveBlog = async () => {
  try {
    const token = getToken();
    if (!token) return;

    if (!blogForm.title.trim()) {
      alert("Blog title is required");
      return;
    }

    if (!blogForm.content.trim()) {
      alert("Blog content is required");
      return;
    }
const payload = {
  title: blogForm.title.trim(),
  isFeatured: blogForm.isFeatured,
  slug: blogForm.slug.trim() || makeSlug(blogForm.title),
  excerpt: blogForm.excerpt,
  content: blogForm.content,
  coverImageUrl: blogForm.coverImageUrl,
  authorName: blogForm.authorName || "ReigniteSpirit",
  status: blogForm.status,
  categoryId: blogForm.categoryId || null,

  // SEO METADATA
  seoTitle: (blogForm.seoTitle || "").trim(),
  seoDescription: (blogForm.seoDescription || "").trim(),
  primaryKeyword: (blogForm.primaryKeyword || "").trim(),
};

    if (blogForm.id) {
      await axios.put(`${API}/api/admin/blog/${blogForm.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } else {
      await axios.post(`${API}/api/admin/blog`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
    }

    setBlogForm({
  id: null,
  isFeatured: false,
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  coverImageUrl: "",
  authorName: "ReigniteSpirit",
  status: "draft",
  categoryId: "",

  // SEO METADATA
  seoTitle: "",
  seoDescription: "",
  primaryKeyword: "",

  // PHASE 6B - ARTICLE QUALITY
  researchQualityScore: null,
  seoQualityScore: null,
  careerRelevanceScore: null,
  originalityScore: null,
  sourceAuthorityScore: null,
  conversionPotentialScore: null,
  articleQualityScore: null,
  qualityRecommendation: "",
  qualityEvaluatedAt: null,

  // PHASE 2 - GROUNDED SOURCES
  sources: [],
});
    await loadBlogs();
    alert("Blog saved successfully");
  } catch (err) {
    console.error(err);
    alert(err?.response?.data || "Failed to save blog");
  }
};

const editBlog = (post) => {
  console.log("EDIT BLOG POST:", post);
  console.log("EDIT BLOG SOURCES:", post.sources);

  setBlogForm({
    id: post.id,
    isFeatured: post.isFeatured || post.is_featured || false,
    title: post.title || "",
    slug: post.slug || "",
    excerpt: post.excerpt || "",
    content: post.content || "",
    coverImageUrl: post.coverImageUrl || post.cover_image_url || "",
    authorName: post.authorName || post.author_name || "ReigniteSpirit",
    status: post.status || "draft",
    categoryId: post.categoryId || post.category_id || "",

    seoTitle: post.seoTitle || post.seo_title || "",
    seoDescription: post.seoDescription || post.seo_description || "",
    primaryKeyword: post.primaryKeyword || post.primary_keyword || "",

    // PHASE 6B - ARTICLE QUALITY
    researchQualityScore:
      post.researchQualityScore ?? post.research_quality_score ?? null,
    seoQualityScore:
      post.seoQualityScore ?? post.seo_quality_score ?? null,
    careerRelevanceScore:
      post.careerRelevanceScore ?? post.career_relevance_score ?? null,
    originalityScore:
      post.originalityScore ?? post.originality_score ?? null,
    sourceAuthorityScore:
      post.sourceAuthorityScore ?? post.source_authority_score ?? null,
    conversionPotentialScore:
      post.conversionPotentialScore ?? post.conversion_potential_score ?? null,
    articleQualityScore:
      post.articleQualityScore ?? post.article_quality_score ?? null,
    qualityRecommendation:
      post.qualityRecommendation || post.quality_recommendation || "",
    qualityEvaluatedAt:
      post.qualityEvaluatedAt || post.quality_evaluated_at || null,

    sources: Array.isArray(post.sources) ? post.sources : [],
  });

  window.scrollTo({ top: 0, behavior: "smooth" });
};const loadPendingComments = useCallback(async () => {
  try {
    setLoadingComments(true);

    const token = getToken();
    if (!token) return;

    const res = await axios.get(`${API}/api/admin/comments/pending`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    setPendingComments(res.data || []);
  } catch (err) {
    console.error("Failed to load comments", err);
    setPendingComments([]);
  } finally {
    setLoadingComments(false);
  }
}, []);

useEffect(() => {
  loadPendingComments();
}, [loadPendingComments]);

const approveComment = async (id) => {
  const token = getToken();
  if (!token) return;

  await axios.put(
    `${API}/api/admin/comments/${id}/approve`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );

  await loadPendingComments();
};

const rejectComment = async (id) => {
  const token = getToken();
  if (!token) return;

  await axios.put(
    `${API}/api/admin/comments/${id}/reject`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );

  await loadPendingComments();
};

const deleteComment = async (id) => {
  if (!window.confirm("Delete this comment permanently?")) return;

  const token = getToken();
  if (!token) return;

  await axios.delete(`${API}/api/admin/comments/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  await loadPendingComments();
};

// =====================================================
// PHASE 2 - BLOG SOURCE VERIFICATION
// =====================================================

const updateSourceStatusLocally = (
  sourceId,
  status
) => {
  setBlogForm((previous) => ({
    ...previous,
    sources: Array.isArray(previous.sources)
      ? previous.sources.map((source) =>
          source.id === sourceId
            ? {
                ...source,
                verificationStatus: status,
                verifiedAt: new Date().toISOString(),
              }
            : source
        )
      : [],
  }));

  setBlogPosts((previousPosts) =>
    previousPosts.map((post) => ({
      ...post,
      sources: Array.isArray(post.sources)
        ? post.sources.map((source) =>
            source.id === sourceId
              ? {
                  ...source,
                  verificationStatus: status,
                  verifiedAt: new Date().toISOString(),
                }
              : source
          )
        : post.sources,
    }))
  );
};


const verifyBlogSource = async (
  sourceId
) => {
  try {
    const token = getToken();

    if (!token) {
      alert("Admin session expired. Please login again.");
      navigate("/admin-login");
      return;
    }

    await axios.put(
      `${API}/api/admin/blog/sources/${sourceId}/verify`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    updateSourceStatusLocally(
      sourceId,
      "verified"
    );

    await loadBlogs();

    alert("Source verified successfully.");
  }
  catch (err) {
    console.error(
      "VERIFY BLOG SOURCE ERROR:",
      err
    );

    const response =
      err?.response?.data;

    const message =
      response?.message ||
      response?.detail ||
      (
        typeof response === "string"
          ? response
          : null
      ) ||
      err?.message ||
      "Failed to verify source.";

    alert(message);
  }
};


const rejectBlogSource = async (
  sourceId
) => {
  try {
    const token = getToken();

    if (!token) {
      alert("Admin session expired. Please login again.");
      navigate("/admin-login");
      return;
    }

    await axios.put(
      `${API}/api/admin/blog/sources/${sourceId}/reject`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    updateSourceStatusLocally(
      sourceId,
      "rejected"
    );

    await loadBlogs();

    alert("Source rejected successfully.");
  }
  catch (err) {
    console.error(
      "REJECT BLOG SOURCE ERROR:",
      err
    );

    const response =
      err?.response?.data;

    const message =
      response?.message ||
      response?.detail ||
      (
        typeof response === "string"
          ? response
          : null
      ) ||
      err?.message ||
      "Failed to reject source.";

    alert(message);
  }
};



// =====================================================
// PHASE 6B - ARTICLE QUALITY / SOURCE SCREENING
// =====================================================

const getQualityRecommendationClass = (recommendation) => {
  switch (String(recommendation || "").toUpperCase()) {
    case "EXCELLENT":
      return "bg-green-600/20 text-green-300 border-green-500/30";
    case "READY_FOR_REVIEW":
      return "bg-blue-600/20 text-blue-300 border-blue-500/30";
    case "NEEDS_REVIEW":
      return "bg-yellow-600/20 text-yellow-300 border-yellow-500/30";
    case "REWORK_RECOMMENDED":
      return "bg-red-600/20 text-red-300 border-red-500/30";
    default:
      return "bg-white/10 text-white/60 border-white/10";
  }
};

const getScreeningRecommendationClass = (recommendation) => {
  switch (String(recommendation || "").toUpperCase()) {
    case "TRUSTED":
      return "bg-green-600/20 text-green-300 border-green-500/30";
    case "REJECT_RECOMMENDED":
      return "bg-red-600/20 text-red-300 border-red-500/30";
    default:
      return "bg-yellow-600/20 text-yellow-300 border-yellow-500/30";
  }
};

const syncEditedBlogFromList = (postId, posts) => {
  const refreshed = (posts || []).find((post) => post.id === postId);
  if (!refreshed) return;

  setBlogForm((previous) => ({
    ...previous,
    researchQualityScore:
      refreshed.researchQualityScore ?? refreshed.research_quality_score ?? null,
    seoQualityScore:
      refreshed.seoQualityScore ?? refreshed.seo_quality_score ?? null,
    careerRelevanceScore:
      refreshed.careerRelevanceScore ?? refreshed.career_relevance_score ?? null,
    originalityScore:
      refreshed.originalityScore ?? refreshed.originality_score ?? null,
    sourceAuthorityScore:
      refreshed.sourceAuthorityScore ?? refreshed.source_authority_score ?? null,
    conversionPotentialScore:
      refreshed.conversionPotentialScore ?? refreshed.conversion_potential_score ?? null,
    articleQualityScore:
      refreshed.articleQualityScore ?? refreshed.article_quality_score ?? null,
    qualityRecommendation:
      refreshed.qualityRecommendation || refreshed.quality_recommendation || "",
    qualityEvaluatedAt:
      refreshed.qualityEvaluatedAt || refreshed.quality_evaluated_at || null,
    sources: Array.isArray(refreshed.sources) ? refreshed.sources : previous.sources,
  }));
};

const refreshBlogsAndCurrentEditor = async (postId) => {
  const token = getToken();
  if (!token) return;

  const res = await axios.get(`${API}/api/admin/blog`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  const posts = Array.isArray(res.data) ? res.data : [];
  setBlogPosts(posts);
  syncEditedBlogFromList(postId, posts);
};

const evaluateBlogQuality = async () => {
  try {
    if (!blogForm.id) {
      alert("Open an existing blog first.");
      return;
    }

    const token = getToken();
    if (!token) {
      alert("Admin session expired. Please login again.");
      navigate("/admin-login");
      return;
    }

    setEvaluatingQuality(true);

    const res = await axios.post(
      `${API}/api/admin/blog/${blogForm.id}/evaluate-quality`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    await refreshBlogsAndCurrentEditor(blogForm.id);

    const score =
      res?.data?.quality?.articleQualityScore ??
      res?.data?.quality?.ArticleQualityScore;

    const recommendation =
      res?.data?.quality?.recommendation ??
      res?.data?.quality?.Recommendation;

    alert(
      "Article quality evaluation completed." +
        (score != null ? `\n\nScore: ${score}/100` : "") +
        (recommendation ? `\nRecommendation: ${recommendation}` : "")
    );
  } catch (err) {
    console.error("EVALUATE BLOG QUALITY ERROR:", err);

    const response = err?.response?.data;
    const message =
      response?.message ||
      response?.detail ||
      (typeof response === "string" ? response : null) ||
      err?.message ||
      "Unable to evaluate article quality.";

    alert(message);
  } finally {
    setEvaluatingQuality(false);
  }
};

const rescreenBlogSources = async () => {
  try {
    if (!blogForm.id) {
      alert("Open an existing blog first.");
      return;
    }

    const token = getToken();
    if (!token) {
      alert("Admin session expired. Please login again.");
      navigate("/admin-login");
      return;
    }

    setScreeningSources(true);

    const res = await axios.post(
      `${API}/api/admin/blog/${blogForm.id}/screen-sources`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    await refreshBlogsAndCurrentEditor(blogForm.id);

    alert(
      `Source screening completed.\n\nScreened sources: ${
        res?.data?.screenedSourceCount ?? 0
      }`
    );
  } catch (err) {
    console.error("RE-SCREEN BLOG SOURCES ERROR:", err);

    const response = err?.response?.data;
    const message =
      response?.message ||
      response?.detail ||
      (typeof response === "string" ? response : null) ||
      err?.message ||
      "Unable to screen blog sources.";

    alert(message);
  } finally {
    setScreeningSources(false);
  }
};

const approveBlog = async (id) => {
  try {
    const token = getToken();
    if (!token) return;

    await axios.put(
      `${API}/api/admin/blog/${id}/approve`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );

    alert("Blog approved successfully.");
    await loadBlogs();
  } catch (err) {
  console.error(
    "APPROVE BLOG ERROR:",
    err
  );

  const response =
    err?.response?.data;

  const message =
    response?.message ||
    response?.detail ||
    (
      typeof response === "string"
        ? response
        : null
    ) ||
    err?.message ||
    "Failed to approve blog";

  const requirement =
    response?.requirement
      ? `\n\n${response.requirement}`
      : "";

  const sourceInfo =
    response?.totalSources != null
      ? `\n\nTotal Sources: ${response.totalSources}\nVerified Sources: ${response.verifiedSources ?? 0}`
      : "";

  alert(
    message +
    requirement +
    sourceInfo
  );
}
}

const rejectBlog = async (id) => {
  try {
    const token = getToken();
    if (!token) return;

    await axios.put(
      `${API}/api/admin/blog/${id}/reject`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );

    alert("Blog rejected successfully.");
    await loadBlogs();
  } catch (err) {
    console.error(err);
    alert(err?.response?.data || "Failed to reject blog");
  }
};
const deleteBlog = async (id) => {
  if (!window.confirm("Delete this blog post?")) return;

  const token = getToken();
  if (!token) return;

  await axios.delete(`${API}/api/admin/blog/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  await loadBlogs();
};
  // =====================================================
  // LOGOUT
  // =====================================================
  const logout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin-login");
  };

  const paymentBadgeClass = (status) => {
    switch (status) {
      case "Paid":
        return "bg-green-600/20 text-green-300";
      case "Pending":
        return "bg-yellow-600/20 text-yellow-300";
      case "Refunded":
        return "bg-orange-600/20 text-orange-300";
      default:
        return "bg-red-600/20 text-red-300";
    }
  };

  const reportBadgeClass = (status) => {
    switch (status) {
      case "Completed":
        return "bg-green-600/20 text-green-300";
      case "Processing":
        return "bg-blue-600/20 text-blue-300";
      case "Failed":
        return "bg-red-600/20 text-red-300";
      default:
        return "bg-gray-600/20 text-gray-300";
    }
  };


  const getSourceStatusClass = (
    status
  ) => {
    switch (
      String(status || "pending")
        .toLowerCase()
    ) {
      case "verified":
        return "bg-green-600/20 text-green-300 border-green-500/30";

      case "rejected":
        return "bg-red-600/20 text-red-300 border-red-500/30";

      default:
        return "bg-yellow-600/20 text-yellow-300 border-yellow-500/30";
    }
  };


  return (
    <div className="min-h-screen bg-[#0b0b16] text-white font-audiowide">
      <header className="w-full bg-[#11111f]/90 border-b border-[#EB6AB5]/20 px-6 py-4 flex justify-between items-center">
        <h1 className="text-[#EB6AB5] text-xl">ADMIN PANEL</h1>

        <button
          onClick={logout}
          className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20"
        >
          Logout
        </button>
      </header>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* WHITELIST */}
        
        {/* USER LOOKUP */}
        <div className="bg-[#11111f] p-6 rounded-xl border border-white/10">
          <h3 className="text-[#EB6AB5] mb-4 text-lg">User Lookup</h3>

          <div className="flex gap-3">
            <input
              value={lookupEmail}
              onChange={(e) => setLookupEmail(e.target.value)}
              placeholder="Enter email"
              className="flex-1 px-4 py-2 rounded-lg bg-black/20 border border-white/10 outline-none"
            />
            <button
              onClick={() => lookupUser()}
              className="px-4 py-2 rounded-lg bg-[#EB6AB5] text-black hover:opacity-90"
            >
              Lookup
            </button>
          </div>

          {lookupResult && (
            <div className="mt-6 space-y-2">
              <p><b>Email:</b> {lookupResult.email}</p>
              <p><b>Name:</b> {lookupResult.name}</p>
              <p><b>Plan:</b> {lookupResult.plan}</p>
              <p><b>Payment:</b> {lookupResult.paymentStatus}</p>
              <p><b>Report Status:</b> {lookupResult.reportStatus}</p>

              <div className="mt-4">
                <b>Original Report:</b>{" "}
                {lookupResult.pdfUrl ? (
                  <button
                    onClick={handleViewOriginalReport}
                    className="ml-2 px-3 py-1 rounded bg-blue-600/20 text-blue-300 hover:bg-blue-600/30"
                  >
                    View Report
                  </button>
                ) : (
                  "Not Available"
                )}
              </div>

              <div className="mt-4">
                <h4 className="text-[#EB6AB5] mb-2">Regenerated Reports</h4>

                {loadingRegen ? (
                  <p className="text-white/70">Loading regenerated reports...</p>
                ) : regenReports.length > 0 ? (
                  <div className="space-y-2">
                    {regenReports.map((r) => (
                      <div key={r.id} className="p-2 rounded bg-white/5">
                        <a
                          href={r.pdfUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-300 hover:underline"
                        >
                          Version {r.version}
                        </a>{" "}
                        <span className="text-white/60">
                          ({new Date(r.createdAt).toLocaleString()})
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/60">No regenerated reports yet</p>
                )}
              </div>
<div className="mt-6 p-4 rounded-lg bg-white/5 border border-white/10">
  <h4 className="text-[#EB6AB5] mb-3">Questionnaire Progress</h4>

  <p><b>Status:</b> {lookupResult.questionnaireStatus || "Not started"}</p>
  <p><b>Current Step:</b> {lookupResult.currentStep ?? "-"}</p>
  <p><b>Current SubStep:</b> {lookupResult.currentSubStep ?? "-"}</p>
  <p><b>Current Question:</b> {lookupResult.currentQuestionKey || "-"}</p>
  <p><b>Last Answered:</b> {lookupResult.lastAnsweredQuestionKey || "-"}</p>
  <p><b>Exit Reason:</b> {lookupResult.exitReason || "-"}</p>
  <p>
    <b>Last Activity:</b>{" "}
    {lookupResult.lastActivityAt
      ? new Date(lookupResult.lastActivityAt).toLocaleString()
      : "-"}
  </p>
  <p>
    <b>Logged Out At:</b>{" "}
    {lookupResult.loggedOutAt
      ? new Date(lookupResult.loggedOutAt).toLocaleString()
      : "-"}
  </p>
</div>
              {lookupResult.paymentStatus === "Paid" && (
                <button
                  disabled={regenerating}
                  onClick={regenerateReport}
                  className="mt-4 px-4 py-2 rounded-lg bg-[#EB6AB5] text-black hover:opacity-90 disabled:opacity-50"
                >
                  {regenerating ? "Regenerating..." : "Regenerate Report"}
                </button>
              )}
            </div>
          )}
        </div>
        {/* BLOG CATEGORIES */}
<div className="bg-[#11111f] p-6 rounded-xl border border-white/10">
  <h3 className="text-[#EB6AB5] mb-4 text-lg">Blog Categories</h3>

  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
    <input
      value={categoryForm.name}
      onChange={(e) =>
        setCategoryForm({
          ...categoryForm,
          name: e.target.value,
          slug: categoryForm.slug || makeSlug(e.target.value),
        })
      }
      placeholder="Category name"
      className="px-4 py-2 rounded-lg bg-black/20 border border-white/10 outline-none"
    />

    <input
      value={categoryForm.slug}
      onChange={(e) =>
        setCategoryForm({ ...categoryForm, slug: e.target.value })
      }
      placeholder="Category slug"
      className="px-4 py-2 rounded-lg bg-black/20 border border-white/10 outline-none"
    />

    <button
      onClick={saveBlogCategory}
      className="px-5 py-2 rounded-lg bg-[#EB6AB5] text-black hover:opacity-90"
    >
      Add Category
    </button>
  </div>

  <textarea
    value={categoryForm.description}
    onChange={(e) =>
      setCategoryForm({ ...categoryForm, description: e.target.value })
    }
    placeholder="Category description"
    rows={2}
    className="mt-3 w-full px-4 py-2 rounded-lg bg-black/20 border border-white/10 outline-none"
  />

  <div className="mt-4 flex flex-wrap gap-2">
    {blogCategories.map((cat) => (
      <span key={cat.id} className="px-3 py-1 rounded-full bg-white/10 text-sm">
        {cat.name}
      </span>
    ))}
  </div>
</div>

{/* BLOG EDITOR */}
<div className="bg-[#11111f] p-6 rounded-xl border border-white/10">
  <div className="flex justify-between items-center mb-4">
    <h3 className="text-[#EB6AB5] text-lg">Blog Editor</h3>

    <button
      onClick={loadBlogs}
      className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20"
    >
      {loadingBlogs ? "Loading..." : "Refresh Blogs"}
    </button>
  </div>

  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
    <input
      value={blogForm.title}
      onChange={(e) =>
        setBlogForm({
          ...blogForm,
          title: e.target.value,
          slug: blogForm.slug || makeSlug(e.target.value),
        })
      }
      placeholder="Blog title"
      className="px-4 py-2 rounded-lg bg-black/20 border border-white/10 outline-none"
    />

    <input
      value={blogForm.slug}
      onChange={(e) => setBlogForm({ ...blogForm, slug: e.target.value })}
      placeholder="URL slug"
      className="px-4 py-2 rounded-lg bg-black/20 border border-white/10 outline-none"
    />

    <input
      value={blogForm.coverImageUrl}
      onChange={(e) =>
        setBlogForm({ ...blogForm, coverImageUrl: e.target.value })
      }
      placeholder="Cover image URL"
      className="px-4 py-2 rounded-lg bg-black/20 border border-white/10 outline-none"
    />
<select
  value={blogForm.categoryId}
  onChange={(e) => setBlogForm({ ...blogForm, categoryId: e.target.value })}
  className="px-4 py-2 rounded-lg bg-black/20 border border-white/10 outline-none"
>
  <option value="">Select Category</option>
  {blogCategories.map((cat) => (
    <option key={cat.id} value={cat.id}>
      {cat.name}
    </option>
  ))}
</select>
<label className="flex items-center gap-3 px-4 py-3 rounded-lg bg-black/20 border border-white/10">
  <input
    type="checkbox"
    checked={blogForm.isFeatured}
    onChange={(e) =>
      setBlogForm({ ...blogForm, isFeatured: e.target.checked })
    }
  />
  <span>Featured Article</span>
</label>
    <select
      value={blogForm.status}
      onChange={(e) => setBlogForm({ ...blogForm, status: e.target.value })}
      className="px-4 py-2 rounded-lg bg-black/20 border border-white/10 outline-none"
    >
      <option value="draft">Draft</option>
      <option value="pending">Pending Approval</option>
      <option value="published">Published</option>
      <option value="rejected">Rejected</option>
    </select>
  </div>
  <textarea
    value={blogForm.excerpt}
    onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })}
    placeholder="Short excerpt"
    rows={3}
    className="mt-3 w-full px-4 py-2 rounded-lg bg-black/20 border border-white/10 outline-none"
  />

  <textarea
    value={blogForm.content}
    onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })}
    placeholder="Full blog content"
    rows={10}
    className="mt-3 w-full px-4 py-2 rounded-lg bg-black/20 border border-white/10 outline-none"
  />

  {/* SEO METADATA */}
  <div className="mt-6 p-4 rounded-xl bg-black/20 border border-[#EB6AB5]/20">
    <h4 className="text-[#EB6AB5] mb-4 text-base">
      SEO Metadata
    </h4>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div>
        <label className="block text-sm text-white/70 mb-2">
          SEO Title
        </label>

        <input
          type="text"
          value={blogForm.seoTitle}
          maxLength={70}
          onChange={(e) =>
            setBlogForm({
              ...blogForm,
              seoTitle: e.target.value,
            })
          }
          placeholder="SEO title"
          className="w-full px-4 py-2 rounded-lg bg-black/20 border border-white/10 outline-none"
        />

        <p className="mt-1 text-xs text-white/40">
          {blogForm.seoTitle.length}/70
        </p>
      </div>

      <div>
        <label className="block text-sm text-white/70 mb-2">
          Primary Keyword
        </label>

        <input
          type="text"
          value={blogForm.primaryKeyword}
          maxLength={100}
          onChange={(e) =>
            setBlogForm({
              ...blogForm,
              primaryKeyword: e.target.value,
            })
          }
          placeholder="Example: AI jobs in Dubai"
          className="w-full px-4 py-2 rounded-lg bg-black/20 border border-white/10 outline-none"
        />

        <p className="mt-1 text-xs text-white/40">
          One focused search phrase only.
        </p>
      </div>
    </div>

    <div className="mt-3">
      <label className="block text-sm text-white/70 mb-2">
        SEO Description
      </label>

      <textarea
        value={blogForm.seoDescription}
        maxLength={180}
        onChange={(e) =>
          setBlogForm({
            ...blogForm,
            seoDescription: e.target.value,
          })
        }
        placeholder="SEO meta description"
        rows={3}
        className="w-full px-4 py-2 rounded-lg bg-black/20 border border-white/10 outline-none"
      />

      <p className="mt-1 text-xs text-white/40">
        {blogForm.seoDescription.length}/180
      </p>
    </div>
  </div>

  {/* PHASE 6B - ARTICLE QUALITY */}
  {blogForm.id && (
    <div className="mt-6 p-4 rounded-xl bg-black/20 border border-purple-400/20">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h4 className="text-purple-300 text-base">Article Quality</h4>
          <p className="text-xs text-white/50 mt-1">
            Advisory AI quality evaluation. Publishing still requires human review.
          </p>
        </div>

        <button
          type="button"
          onClick={evaluateBlogQuality}
          disabled={evaluatingQuality}
          className="px-4 py-2 rounded-lg bg-purple-600/20 text-purple-300 hover:bg-purple-600/30 border border-purple-500/20 disabled:opacity-50"
        >
          {evaluatingQuality
            ? "Evaluating..."
            : blogForm.articleQualityScore == null
            ? "Evaluate Quality"
            : "Re-Evaluate Quality"}
        </button>
      </div>

      {blogForm.articleQualityScore == null ? (
        <div className="p-4 rounded-lg bg-white/5 border border-white/10">
          <p className="text-white/60">This article has not been evaluated yet.</p>
        </div>
      ) : (
        <>
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
            <div className="text-4xl font-bold text-white">
              {blogForm.articleQualityScore}
              <span className="text-lg text-white/40"> / 100</span>
            </div>

            <span
              className={`inline-flex w-fit px-3 py-1 rounded-full border text-xs ${getQualityRecommendationClass(
                blogForm.qualityRecommendation
              )}`}
            >
              {blogForm.qualityRecommendation || "NOT_EVALUATED"}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              ["Research Quality", blogForm.researchQualityScore, 25],
              ["SEO Quality", blogForm.seoQualityScore, 20],
              ["Career Relevance", blogForm.careerRelevanceScore, 20],
              ["Originality", blogForm.originalityScore, 15],
              ["Source Authority", blogForm.sourceAuthorityScore, 10],
              ["Conversion Potential", blogForm.conversionPotentialScore, 10],
            ].map(([label, value, max]) => (
              <div
                key={label}
                className="p-3 rounded-lg bg-white/5 border border-white/10"
              >
                <p className="text-xs text-white/50">{label}</p>
                <p className="mt-1 text-lg font-semibold">
                  {value ?? "-"}
                  <span className="text-sm text-white/40"> / {max}</span>
                </p>
              </div>
            ))}
          </div>

          <p className="mt-4 text-xs text-white/40">
            Last evaluated:{" "}
            {blogForm.qualityEvaluatedAt
              ? new Date(blogForm.qualityEvaluatedAt).toLocaleString()
              : "-"}
          </p>
        </>
      )}
    </div>
  )}

  {/* PHASE 2 - GROUNDED RESEARCH SOURCES */}
  {blogForm.id && (
    <div className="mt-6 p-4 rounded-xl bg-black/20 border border-cyan-400/20">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-4">
        <div>
          <h4 className="text-cyan-300 text-base">
            Grounded Research Sources
          </h4>
          <p className="text-xs text-white/50 mt-1">
            Review these sources before publishing the article.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={rescreenBlogSources}
            disabled={screeningSources}
            className="px-3 py-2 rounded-lg bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 border border-cyan-400/20 text-sm disabled:opacity-50"
          >
            {screeningSources ? "Screening..." : "Re-Screen Sources"}
          </button>

          <span className="text-xs px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-400/20">
            {Array.isArray(blogForm.sources) ? blogForm.sources.length : 0} source
            {(Array.isArray(blogForm.sources) ? blogForm.sources.length : 0) === 1 ? "" : "s"}
          </span>
        </div>
      </div>

      {!Array.isArray(blogForm.sources) || blogForm.sources.length === 0 ? (
        <div className="p-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white/50">
          No grounded sources are attached to this article.
        </div>
      ) : (
        <div className="space-y-3">
          {blogForm.sources.map((source, index) => (
            <div
              key={source.id || `${source.url || "source"}-${index}`}
              className="p-3 rounded-lg bg-white/5 border border-white/10"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-white">
                    {index + 1}.{" "}
                    {source.title ||
                      source.publisher ||
                      `Source ${index + 1}`}
                  </p>

                  {source.publisher && (
                    <p className="text-xs text-white/50 mt-1">
                      Publisher: {source.publisher}
                    </p>
                  )}

                  {source.publishedDate && (
                    <p className="text-xs text-white/40 mt-1">
                      Published:{" "}
                      {new Date(source.publishedDate).toLocaleDateString()}
                    </p>
                  )}

                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="px-2 py-1 rounded border border-white/10 bg-white/5 text-xs text-white/70">
                      Authority: {source.authorityTier || "Not screened"}
                    </span>

                    <span className="px-2 py-1 rounded border border-white/10 bg-white/5 text-xs text-white/70">
                      Trust: {source.trustScore != null ? `${source.trustScore}/100` : "Not screened"}
                    </span>

                    {source.screeningRecommendation && (
                      <span
                        className={`px-2 py-1 rounded border text-xs ${getScreeningRecommendationClass(
                          source.screeningRecommendation
                        )}`}
                      >
                        System: {source.screeningRecommendation}
                      </span>
                    )}
                  </div>

                  {source.screeningReason && (
                    <p className="text-xs text-white/50 mt-2">
                      Screening reason: {source.screeningReason}
                    </p>
                  )}

                  {source.screenedAt && (
                    <p className="text-xs text-white/30 mt-1">
                      Screened: {new Date(source.screenedAt).toLocaleString()}
                    </p>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2 shrink-0">
                  <span
                    className={`px-3 py-1 rounded-full border text-xs ${getSourceStatusClass(
                      source.verificationStatus
                    )}`}
                  >
                    {source.verificationStatus || "pending"}
                  </span>

                  {(source.verificationStatus || "pending").toLowerCase() !== "verified" && (
                    <button
                      type="button"
                      onClick={() => verifyBlogSource(source.id)}
                      className="px-3 py-2 rounded-lg bg-green-600/20 text-green-300 hover:bg-green-600/30 border border-green-500/20 text-sm"
                    >
                      Verify
                    </button>
                  )}

                  {(source.verificationStatus || "pending").toLowerCase() !== "rejected" && (
                    <button
                      type="button"
                      onClick={() => rejectBlogSource(source.id)}
                      className="px-3 py-2 rounded-lg bg-red-600/20 text-red-300 hover:bg-red-600/30 border border-red-500/20 text-sm"
                    >
                      Reject Source
                    </button>
                  )}

                  {source.url ? (
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-3 py-2 rounded-lg bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 border border-cyan-400/20 text-sm"
                    >
                      Open Source ↗
                    </a>
                  ) : (
                    <span className="text-xs text-white/40">
                      URL unavailable
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )}

  <div className="flex gap-3 mt-4">
    <button
      onClick={saveBlog}
      className="px-5 py-2 rounded-lg bg-[#EB6AB5] text-black hover:opacity-90"
    >
      {blogForm.id ? "Update Blog" : "Create Blog"}
    </button>

    {blogForm.id && (
      <button
        onClick={() =>
          setBlogForm({
  id: null,
  isFeatured: false,
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  coverImageUrl: "",
  authorName: "ReigniteSpirit",
  status: "draft",
  categoryId: "",

  // SEO METADATA
  seoTitle: "",
  seoDescription: "",
  primaryKeyword: "",

  // PHASE 6B - ARTICLE QUALITY
  researchQualityScore: null,
  seoQualityScore: null,
  careerRelevanceScore: null,
  originalityScore: null,
  sourceAuthorityScore: null,
  conversionPotentialScore: null,
  articleQualityScore: null,
  qualityRecommendation: "",
  qualityEvaluatedAt: null,

  // PHASE 2 - GROUNDED SOURCES
  sources: [],
})
        }
        className="px-5 py-2 rounded-lg bg-white/10 hover:bg-white/20"
      >
        Cancel Edit
      </button>
    )}
  </div>

  <div className="mt-6 space-y-2">
    {blogPosts.length === 0 ? (
      <p className="text-white/60">No blog posts found</p>
    ) : (
      blogPosts.map((post) => {
        const status = (post.status || "").toLowerCase();
        const canReview = status === "draft" || status === "pending";

        return (
          <div
            key={post.id}
            className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-3 rounded-lg bg-white/5 border border-white/10"
          >
            <div>
              <p className="font-semibold">{post.title}</p>
              <p className="text-xs text-white/50">
                /blog/{post.slug} · {post.status}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {canReview && (
                <>
                  <button
                    type="button"
                    onClick={() => approveBlog(post.id)}
                    className="px-3 py-1 rounded bg-green-600/20 text-green-300 hover:bg-green-600/30 border border-green-500/20"
                  >
                    Publish
                  </button>

                  <button
                    type="button"
                    onClick={() => rejectBlog(post.id)}
                    className="px-3 py-1 rounded bg-yellow-600/20 text-yellow-300 hover:bg-yellow-600/30 border border-yellow-500/20"
                  >
                    Reject
                  </button>
                </>
              )}

              <button
                type="button"
                onClick={() => editBlog(post)}
                className="px-3 py-1 rounded bg-blue-600/20 text-blue-300 hover:bg-blue-600/30"
              >
                Edit
              </button>

              <button
                type="button"
                onClick={() => deleteBlog(post.id)}
                className="px-3 py-1 rounded bg-red-600/20 text-red-300 hover:bg-red-600/30"
              >
                Delete
              </button>
            </div>
          </div>
        );
      })
    )}
  </div>
</div>
{/* COMMENT MODERATION */}
<div className="bg-[#11111f] p-6 rounded-xl border border-white/10">
  <div className="flex justify-between items-center mb-4">
    <h3 className="text-[#EB6AB5] text-lg">Pending Comments</h3>

    <button
      onClick={loadPendingComments}
      className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20"
    >
      {loadingComments ? "Loading..." : "Refresh Comments"}
    </button>
  </div>

  {pendingComments.length === 0 ? (
    <p className="text-white/60">No pending comments.</p>
  ) : (
    <div className="space-y-3">
      {pendingComments.map((c) => (
        <div
          key={c.id}
          className="p-4 rounded-lg bg-white/5 border border-white/10"
        >
          <p className="text-[#EB6AB5] font-semibold">
            {c.userName || c.user_name || "Registered User"}
          </p>

          <p className="text-white/80 mt-2">{c.comment}</p>

          <p className="text-xs text-white/40 mt-2">
            {c.createdAt || c.created_at
              ? new Date(c.createdAt || c.created_at).toLocaleString()
              : "-"}
          </p>

          <div className="flex gap-2 mt-4">
            <button
              onClick={() => approveComment(c.id)}
              className="px-3 py-1 rounded bg-green-600/20 text-green-300 hover:bg-green-600/30"
            >
              Approve
            </button>

            <button
              onClick={() => rejectComment(c.id)}
              className="px-3 py-1 rounded bg-yellow-600/20 text-yellow-300 hover:bg-yellow-600/30"
            >
              Reject
            </button>

            <button
              onClick={() => deleteComment(c.id)}
              className="px-3 py-1 rounded bg-red-600/20 text-red-300 hover:bg-red-600/30"
            >
              Delete
            </button>
          </div>
        </div>
      ))}
    </div>
  )}
</div>
        {/* REGISTERED USERS */}
        <div className="bg-[#11111f] p-6 rounded-xl border border-white/10">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-[#EB6AB5] text-lg">Registered Users</h3>
            <button
              onClick={loadUsers}
              className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20"
            >
              {loadingUsers ? "Loading..." : "Refresh"}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left border-b border-white/10">
                  <th className="py-3 pr-4">Name</th>
                  <th className="py-3 pr-4">Email</th>
                  <th className="py-3 pr-4">Plan</th>
                  <th className="py-3 pr-4">Payment</th>
                  <th className="py-3 pr-4">Report</th>
                  <th className="py-3 pr-4">Original Report</th>
                  <th className="py-3 pr-4">Actions</th>
                  <th className="py-3 pr-4">Questionnaire</th>
<th className="py-3 pr-4">Current Question</th>
<th className="py-3 pr-4">Last Answered</th>
<th className="py-3 pr-4">Exit</th>
<th className="py-3 pr-4">Last Activity</th>
                </tr>
              </thead>

              <tbody>
  {users.length === 0 ? (
    <tr>
<td colSpan="12" className="py-6 text-white/60">
        {loadingUsers ? "Loading users..." : "No users found"}
      </td>
    </tr>
  ) : (
    users.map((user, index) => (
      <tr
        key={user.email || index}
        className="border-b border-white/5 hover:bg-white/5"
      >
        <td className="py-3 pr-4">{user.name || "-"}</td>
        <td className="py-3 pr-4">{user.email}</td>
        <td className="py-3 pr-4">{user.plan}</td>

        <td className="py-3 pr-4">
          <span
            className={`px-2 py-1 rounded text-xs ${paymentBadgeClass(
              user.paymentStatus
            )}`}
          >
            {user.paymentStatus}
          </span>
        </td>

        <td className="py-3 pr-4">
          <span
            className={`px-2 py-1 rounded text-xs ${reportBadgeClass(
              user.reportStatus
            )}`}
          >
            {user.reportStatus}
          </span>
        </td>

        <td className="py-3 pr-4">
          {user.pdfUrl ? (
            <button
              onClick={() => handleViewUserReport(user.pdfUrl)}
              className="px-3 py-1 rounded bg-blue-600/20 text-blue-300 hover:bg-blue-600/30"
            >
              View Report
            </button>
          ) : (
            <span className="text-white/50">Not Available</span>
          )}
        </td>

        <td className="py-3 pr-4">
  <div className="flex flex-col gap-2 min-w-[115px]">

    <button
      type="button"
      onClick={() =>
        handleQuickLookup(
          user
        )
      }
      className="
        px-3
        py-1
        rounded
        bg-[#EB6AB5]/20
        text-[#EB6AB5]
        hover:bg-[#EB6AB5]/30
      "
    >
      Open Details
    </button>

    <button
      type="button"
      onClick={() =>
        deleteRegisteredUser(
          user
        )
      }
      className="
        px-3
        py-1
        rounded
        bg-red-600/20
        text-red-300
        hover:bg-red-600/40
        border
        border-red-500/30
      "
    >
      Delete User
    </button>

  </div>
</td>
        <td className="py-3 pr-4">
          <span className="px-2 py-1 rounded text-xs bg-purple-600/20 text-purple-300">
            {user.questionnaireStatus || "Not started"}
          </span>
        </td>

        <td className="py-3 pr-4">
          {user.currentQuestionKey || "-"}
          {user.currentStep != null && user.currentSubStep != null && (
            <div className="text-xs text-white/50">
              Step {user.currentStep}.{user.currentSubStep}
            </div>
          )}
        </td>

        <td className="py-3 pr-4">{user.lastAnsweredQuestionKey || "-"}</td>

        <td className="py-3 pr-4">{user.exitReason || "-"}</td>

        <td className="py-3 pr-4">
          {user.lastActivityAt
            ? new Date(user.lastActivityAt).toLocaleString()
            : "-"}
        </td>
      </tr>
    ))
  )}
</tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}